// PoterProtokolView — Poterový protokol (denník) anhydrit/cement × RD/Firma.
// Kontrolný list pre zákazníka, min. hrúbka podľa MIXIN TL, podpis prstom, PDF, e-mail. Mirko 2026-07-01.
//
// 2026-08-03 (Mirko): „ak sa na stavbe poserie signál, protokol musí vypísať OFFICE" —
//   • LAYOUT presne ako Obhliadka (tónovaná hlavička + Grp sekcie + Field boxy + oranžové Chips),
//   • POLIA = všetko z poteru v mview (nič sa neuberá),
//   • HLAVIČKA ako obhliadka: názov, zákazník z číselníka, kontakt, telefón, 2× e-mail, adresa, GPS,
//     maps link, termín realizácie + PARTIA z Rozpisu (namiesto „obhliadku robil"; dátum obhliadky NIE),
//   • EDITOVATEĽNÝ — existujúci protokol sa dá otvoriť a prepísať (PUT), fotky sa nestratia,
//   • CHRBTICA — hlavička sa doťahuje zo zákazky → obhliadky/CP/dopytu → Rozpisu (väzby sa ukladajú).
import { KontaktnaOsoba } from '../components/KontaktyPartnera.jsx'   // ústava 131.10 + 0.J — jeden blok kontaktnej osoby (p93)
import React, { useEffect, useMemo, useState } from 'react'
import { useDirtyFlag } from '../hooks/useDirtyState.js'   // straz_v4 (29. 9. 2026): rozpísaný formulár drží spoločnú stráž odchodu (ústava 53.1.b)
import { PageHeader, SearchableSelect } from '../components/Ui.jsx'
import { Btn, BtnAdd, BtnGhost } from '../components/Btn.jsx'
import { Field, FieldGrid, inp } from '../components/DocFields.jsx'
import TelefonInput from '../components/TelefonInput.jsx'
import NoviCustomerDialog from '../components/NoviCustomerDialog.jsx'
import { useToast } from '../components/Toast.jsx'
import { COLORS, TYPO, SPACE, RADIUS, todayLocal } from '../theme.js'
import { useAuth } from '../auth/AuthContext.jsx'
import { fetchAuth } from '../auth/fetchAuth.js'
import EmailReplyBox from '../components/EmailReplyBox.jsx'
import SignaturePad from '../components/SignaturePad.jsx'

const FLOORS_RD = ['Suterén', 'Prízemie', '1.NP', '2.NP', '3.NP', 'Podkrovie', 'Garáž', 'Terasa']
const FLOORS_FIRMA = ['1.PP', '2.PP', 'Prízemie', '1.NP', '2.NP', '3.NP', '4.NP', '5.NP', '6.NP']
const num = (n) => (Math.round((dec(n) || 0) * 100) / 100).toLocaleString('sk-SK')
// Slovenska klavesnica pise desatinnu CIARKU — parseFloat('6,3')=6, Number('6,3')=NaN (Mirko 3.8.: „neberie vycerpane do pdf")
const dec = (v) => { if (v == null || v === '') return null; const n = parseFloat(String(v).replace(',', '.')); return isNaN(n) ? null : n }
const BRAND = COLORS.brand   // vzhlad_v2: značka z témy, bez vlastného odtieňa

// ── prvky prevzaté 1:1 z ObhliadkaEditor (ústava 103.D — rovnaký vzhľad, žiadne vlastné kópie štýlu) ──
function Chips({ opts, val, onPick }) {
  return <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
    {opts.map(o => { const on = val === o.value; return (
      <button key={o.value} type="button" onClick={() => onPick(on ? '' : o.value)} style={{
        padding: '5px 11px', borderRadius: RADIUS.full, fontSize: TYPO.sizes.sm, cursor: 'pointer', fontFamily: 'inherit',
        border: `1px solid ${on ? BRAND : COLORS.line}`, background: on ? BRAND : '#fff', color: on ? '#fff' : COLORS.text,
      }}>{o.label}</button>) })}
  </div>
}
function Grp({ children }) {
  return <div style={{ fontSize: TYPO.sizes.xs, fontWeight: TYPO.weights.bold, color: BRAND, textTransform: 'uppercase', letterSpacing: '0.04em', margin: '14px 0 6px', borderBottom: `1px solid ${COLORS.lineSoft || COLORS.line}`, paddingBottom: 3 }}>{children}</div>
}

// STAVBA_HISTORIA_V1 (Mirko 21. 10. 2026, ksbx-appky 2667): história zmien protokolu — kto, kedy, z čoho na čo (GET /api/poter-protokoly/:id/zmeny, audit_log)
function HistoriaZmien({ id }) {
  const [otv, setOtv] = useState(false); const [z, setZ] = useState(null)
  useEffect(() => { if (!otv || !id) return; fetchAuth('/api/poter-protokoly/' + encodeURIComponent(id) + '/zmeny').then(r => r.ok ? r.json() : null).then(j => setZ((j && j.zmeny) || [])).catch(() => setZ([])) }, [otv, id])
  if (!id) return null
  return <div style={{ marginTop: SPACE.sm }}>
    <button type="button" onClick={() => setOtv(o => !o)} style={{ background: 'none', border: 'none', color: COLORS.nav, fontSize: TYPO.sizes.sm, fontWeight: TYPO.weights.semibold, cursor: 'pointer', fontFamily: 'inherit', padding: 0 }}>{otv ? '▾' : '▸'} História zmien protokolu {id}</button>
    {otv && <div style={{ border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.md, padding: SPACE.sm, marginTop: 6, background: '#fff' }}>
      {z === null && <div style={{ fontSize: TYPO.sizes.sm, color: COLORS.muted }}>Načítavam…</div>}
      {z && !z.length && <div style={{ fontSize: TYPO.sizes.sm, color: COLORS.muted }}>Zatiaľ bez zmien.</div>}
      {(z || []).slice().reverse().map(r => <div key={r.id} style={{ padding: '4px 0', borderBottom: `1px solid ${COLORS.lineSoft || COLORS.line}` }}>
        <div style={{ fontSize: TYPO.sizes.sm, fontWeight: TYPO.weights.semibold }}>{r.kedy_text} · {r.meno} {r.akcia_text}</div>
        {r.polia.length ? r.polia.map((x, i) => <div key={i} style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted }}>{x.text}</div>) : <div style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted }}>{r.text}</div>}
      </div>)}
    </div>}
  </div>
}

export default function PoterProtokolView({ embedded = false, fixedProject = null, onSaved = null, protokolId = null }) {
  const { user } = useAuth()
  const toast = useToast()
  const [meta, setMeta] = useState({ materials: { anhydrit: [], cement: [] }, checklist: { anhydrit: [], cement: [] }, waiver: '' })
  const [projects, setProjects] = useState([])
  const [customers, setCustomers] = useState([])
  const [partie, setPartie] = useState([])
  const [projId, setProjId] = useState(fixedProject?.id || '')
  const [typ, setTyp] = useState('anhydrit')
  const [matCode, setMatCode] = useState('AE25')
  const [objTyp, setObjTyp] = useState('rd')
  const [podlazia, setPodlazia] = useState([{ podlazie: '', dohodnuta_vyska_cm: '', min_hrubka_mm: '', vycerpane_m3: '', plocha_m2: '', popis: '', foto: [] }])
  const [checks, setChecks] = useState({})
  const [popis, setPopis] = useState('')
  const [signature, setSignature] = useState('')
  const [kamirSig, setKamirSig] = useState('')
  const [kamirMeno, setKamirMeno] = useState('')
  const [dodaciList, setDodaciList] = useState('')
  const [teplota, setTeplota] = useState('')
  const [pocasie, setPocasie] = useState('')
  const [pocasieBusy, setPocasieBusy] = useState(false)
  const [signedName, setSignedName] = useState('')
  const [waiverOk, setWaiverOk] = useState(false)
  const [savedId, setSavedId] = useState(null)
  const [diaryWritten, setDiaryWritten] = useState(false)
  const [pdfUrl, setPdfUrl] = useState('')
  const [busy, setBusy] = useState(false)
  // ── HLAVIČKA (ako obhliadka) ──
  const [h, setH] = useState({ nazov: '', customer_id: '', kontakt_meno: '', telefon: '', email: '', email2: '',
    adresa: '', gps_lat: '', gps_lng: '', maps_link: '', termin_real: '', partia_id: '', partia_nazov: '' })
  const setHF = (k, v) => setH(s => ({ ...s, [k]: v }))
  const [novyOpen, setNovyOpen] = useState(false)
  const [novyPrefill, setNovyPrefill] = useState('')
  const [editId, setEditId] = useState(protokolId || null)   // ak je vyplnené → UPRAVUJEME existujúci
  useDirtyFlag(!!editId && !protokolId, { key: 'poter-protokol-uprava', discard: () => { setEditId(null) } })   // straz_v4

  useEffect(() => {
    fetchAuth('/api/poter-protokoly/meta').then(r => r.json()).then(setMeta).catch(() => {})
    fetchAuth('/api/projects').then(r => r.json()).then(d => setProjects(Array.isArray(d) ? d : [])).catch(() => {})
    fetchAuth('/api/customers').then(r => r.ok ? r.json() : []).then(d => setCustomers(Array.isArray(d) ? d : [])).catch(() => {})
    // PARTIE z Rozpisu — predáci/partie, ktoré reálne robia potery (fallback: všetci predáci)
    Promise.all([
      fetchAuth('/api/employees').then(r => r.ok ? r.json() : []).catch(() => []),
      fetchAuth('/api/work-schedule').then(r => r.ok ? r.json() : []).catch(() => []),
      fetchAuth('/api/projects').then(r => r.ok ? r.json() : []).catch(() => []),
    ]).then(([emps, ws, prj]) => {
      const E = Array.isArray(emps) ? emps : [], W = Array.isArray(ws) ? ws : [], P = Array.isArray(prj) ? prj : []
      const poterProj = new Set(P.filter(p => ['poter', 'potery'].includes(String(p.type || '').toLowerCase())).map(p => p.id))
      const naPoter = new Set(W.filter(w => poterProj.has(w.project_id)).map(w => w.employee_id))
      const predaci = E.filter(e => (e.is_partak || e.role === 'predák') && e.active !== 0 && e.active !== '0')
      const vyber = predaci.filter(e => naPoter.has(e.id))
      setPartie((vyber.length ? vyber : predaci)
        .sort((a, b) => `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`, 'sk'))
        .map(e => ({ value: e.id, label: `${e.firstName} ${e.lastName}${e.party ? ' · ' + e.party : ''}` })))
    })
  }, [])

  // ÚPRAVA — načítaj existujúci protokol a predvyplň všetko (Mirko 3.8.)
  useEffect(() => {
    if (!protokolId) return
    fetchAuth('/api/poter-protokoly/' + protokolId).then(r => r.json()).then(d => {
      if (!d || d.error) return
      setEditId(d.id); setProjId(d.project_id || '')
      setTyp(d.typ === 'cement' ? 'cement' : 'anhydrit')
      if (d.material_code) setMatCode(d.material_code)
      setObjTyp(d.objednavatel_typ === 'firma' ? 'firma' : 'rd')
      setPopis(d.popis || ''); setSignedName(d.signed_name || ''); setSignature(d.signature_data || '')
      setKamirSig(d.kamir_signature_data || ''); setKamirMeno(d.kamir_signed_name || '')
      setDodaciList(d.dodaci_list || ''); setTeplota(d.teplota || ''); setPocasie(d.pocasie || '')
      setWaiverOk(!!d.waiver_accepted); setSavedId(d.id); if (d.pdf_file) setPdfUrl('/uploads/' + d.pdf_file)
      setH({ nazov: d.nazov || '', customer_id: d.customer_id || '', kontakt_meno: d.kontakt_meno || '',
        telefon: d.telefon || '', email: d.email || '', email2: d.email2 || '', adresa: d.adresa || '',
        gps_lat: d.gps_lat || '', gps_lng: d.gps_lng || '', maps_link: d.maps_link || '',
        termin_real: d.termin_real || '', partia_id: d.partia_id || '', partia_nazov: d.partia_nazov || '' })
      try { const c = {}; JSON.parse(d.checklist_json || '[]').forEach(it => { c[it.key] = !!it.checked }); setChecks(c) } catch (_) {}
      const pod = Array.isArray(d.podlazia) ? d.podlazia : []
      setPodlazia(pod.length ? pod.map(p => { let foto = []; try { foto = JSON.parse(p.foto_json || '[]') } catch (_) {}
        let fk = []; try { fk = JSON.parse(p.foto_konzistencia_json || '[]') } catch (_) {}   // PROTOKOL_KONZISTENCIA_V1
        return { podlazie: p.podlazie || '', dohodnuta_vyska_cm: p.dohodnuta_vyska_cm ?? '', min_hrubka_mm: p.min_hrubka_mm ?? '',
          vycerpane_m3: p.vycerpane_m3 ?? '', plocha_m2: p.plocha_m2 ?? '', popis: p.popis || '', foto, foto_konzistencia: fk } })
        : [{ podlazie: '', dohodnuta_vyska_cm: '', min_hrubka_mm: '', vycerpane_m3: '', plocha_m2: '', popis: '', foto: [] }])
    }).catch(() => toast.error('Protokol sa nepodarilo načítať'))
  }, [protokolId])

  // typ zmena → reset materiál + checklist (pri úprave sa checklist prepíše až po načítaní)
  useEffect(() => {
    const list = (meta.materials[typ] || [])
    if (!protokolId) setMatCode(typ === 'cement' ? (list[0] && list[0].code) || 'CEMENT' : 'AE25')
    setChecks(prev => { const c = {}; (meta.checklist[typ] || []).forEach(it => { c[it.key] = !!prev[it.key] }); return c })
  }, [typ, meta])

  // zákazka → odvoď RD/Firma + doťiahni hlavičku z chrbtice (nedeštruktívne — prázdne polia)
  useEffect(() => {
    if (fixedProject) { setProjId(fixedProject.id); setObjTyp((fixedProject.segment || '').toLowerCase() === 'rd' ? 'rd' : 'firma') }
    else if (projId) fetchAuth('/api/poter-protokoly/derive/' + projId).then(r => r.json()).then(d => setObjTyp(d.objednavatel_typ || 'rd')).catch(() => {})
  }, [projId, fixedProject && fixedProject.id, fixedProject && fixedProject.segment])

  // predvyplnenie hlavičky zo zákazky + obhliadky (CHRBTICA) — len prázdne polia
  useEffect(() => {
    const pid = projId || (fixedProject && fixedProject.id) || ''
    if (!pid || editId) return
    const p = projects.find(x => String(x.id) === String(pid)) || fixedProject || {}
    setH(s => ({ ...s, nazov: s.nazov || p.name || '', adresa: s.adresa || p.site_address || '',
      customer_id: s.customer_id || p.customerId || '', maps_link: s.maps_link || p.google_maps_link || '',
      gps_lat: s.gps_lat || p.gps_lat || '', gps_lng: s.gps_lng || p.gps_lng || '' }))
    fetchAuth('/api/obhliadky').then(r => r.ok ? r.json() : []).then(list => {
      const ob = (Array.isArray(list) ? list : []).find(o => o.project_id === pid) || null
      if (!ob) return
      setH(s => ({ ...s, kontakt_meno: s.kontakt_meno || ob.kontakt_meno || '', telefon: s.telefon || ob.telefon || '',
        email: s.email || ob.email || '', email2: s.email2 || ob.email2 || '', adresa: s.adresa || ob.adresa || '',
        customer_id: s.customer_id || ob.customer_id || '', termin_real: s.termin_real || ob.termin_real || '',
        gps_lat: s.gps_lat || ob.gps_lat || '', gps_lng: s.gps_lng || ob.gps_lng || '', maps_link: s.maps_link || ob.maps_link || '' }))
    }).catch(() => {})
  }, [projId, projects, fixedProject && fixedProject.id, editId])

  const material = useMemo(() => (meta.materials[typ] || []).find(m => m.code === matCode) || null, [meta, typ, matCode])
  const floors = objTyp === 'firma' ? FLOORS_FIRMA : FLOORS_RD
  const sumM3 = podlazia.reduce((a, p) => a + (dec(p.vycerpane_m3) || 0), 0)
  const sumM2 = podlazia.reduce((a, p) => a + (dec(p.plocha_m2) || 0), 0)
  const projOpts = projects.map(p => ({ value: p.id, label: (p.zakazka_code ? p.zakazka_code + ' · ' : '') + (p.name || p.id) }))

  const setPod = (i, k, v) => setPodlazia(a => a.map((p, j) => j === i ? { ...p, [k]: v } : p))
  const addPod = () => setPodlazia(a => [...a, { podlazie: '', dohodnuta_vyska_cm: '', min_hrubka_mm: '', vycerpane_m3: '', plocha_m2: '', popis: '', foto: [] }])
  const delPod = (i) => setPodlazia(a => a.length > 1 ? a.filter((_, j) => j !== i) : a)

  const uploadFoto = async (i, files) => {
    for (const f of Array.from(files || [])) {
      const fd = new FormData(); fd.append('file', f)
      try { const r = await fetchAuth('/api/poter-protokoly/upload-photo', { method: 'POST', body: fd }); const j = await r.json()
        if (j.url) setPodlazia(a => a.map((p, x) => x === i ? { ...p, foto: [...(p.foto || []), j.url] } : p)) } catch (_) { toast.error('Foto sa nenahralo') }
    }
  }
  const delFoto = (i, k) => setPodlazia(a => a.map((p, x) => x === i ? { ...p, foto: (p.foto || []).filter((_, y) => y !== k) } : p))
  // PROTOKOL_KONZISTENCIA_V1: fotka skúšky konzistencie (rozlievací terč) pri podlaží
  const uploadKonz = async (i, files) => {
    for (const f of Array.from(files || [])) {
      const fd = new FormData(); fd.append('file', f)
      try { const r = await fetchAuth('/api/poter-protokoly/upload-photo', { method: 'POST', body: fd }); const j = await r.json()
        if (j.url) setPodlazia(a => a.map((p, x) => x === i ? { ...p, foto_konzistencia: [...(p.foto_konzistencia || []), j.url] } : p)) } catch (_) { toast.error('Foto sa nenahralo') }
    }
  }
  const delKonz = (i, k) => setPodlazia(a => a.map((p, x) => x === i ? { ...p, foto_konzistencia: (p.foto_konzistencia || []).filter((_, y) => y !== k) } : p))

  // Zákazník z číselníka — doplní LEN prázdne polia (nedeštruktívne, ústava 103.D)
  const pickCustomer = (id) => {
    const c = customers.find(x => x.id === id)
    setH(s => ({ ...s, customer_id: id,
      kontakt_meno: s.kontakt_meno || (c && c.name) || '', telefon: s.telefon || (c && c.phone) || '',
      email: s.email || (c && c.email) || '', email2: s.email2 || (c && c.email2) || '', adresa: s.adresa || (c && c.address) || '' }))
  }

  // ÚSTAVA 25.6 — teplota a počasie sa načítajú SAMY (adresa → GPS zo zákazky → GPS z obhliadky).
  async function loadPocasie(ticho) {
    const p = projects.find(x => String(x.id) === String(projId)) || fixedProject
    const addr = h.adresa || (p && (p.site_address || p.psc || p.obec)) || ''
    setPocasieBusy(true)
    try {
      const d = todayLocal()
      const q = '/api/weather?address=' + encodeURIComponent(addr) + '&date=' + d
        + '&project_id=' + encodeURIComponent(projId || '')
        + (h.gps_lat ? '&lat=' + encodeURIComponent(h.gps_lat) + '&lng=' + encodeURIComponent(h.gps_lng || '') : '')
      const r = await fetchAuth(q)
      const j = await r.json().catch(() => ({}))
      let t = j.current ?? j.temperature ?? j.temp
      if (t == null) t = j.temp_avg
      if (t == null && j.temp_min != null && j.temp_max != null) t = (j.temp_min + j.temp_max) / 2
      if (t != null) setTeplota(Math.round(Number(t)) + ' °C')
      if (j.weather || j.weather_type) setPocasie(String(j.weather || j.weather_type))
      if (t == null && !ticho) toast.error('Teplotu sa nepodarilo načítať — doplň ručne.')
    } catch (_) { if (!ticho) toast.error('Teplotu sa nepodarilo načítať — doplň ručne.') }
    finally { setPocasieBusy(false) }
  }
  useEffect(() => { if (projId && !teplota) loadPocasie(true) }, [projId, h.gps_lat])

  const save = async () => {
    if (!projId) { toast.error('Vyber zákazku (povinná)'); return }
    if (typ === 'anhydrit' && !matCode) { toast.error('Vyber materiál'); return }
    if (!waiverOk) { toast.error('Zákazník musí odsúhlasiť podmienky'); return }
    setBusy(true)
    try {
      const checklist = (meta.checklist[typ] || []).map(it => ({ key: it.key, label: it.label, checked: !!checks[it.key] }))
      const body = { project_id: projId, typ, material_code: matCode, objednavatel_typ: objTyp, checklist, podlazia, popis,
        waiver_accepted: 1, signature_data: signature, signed_name: signedName,
        kamir_signature_data: kamirSig, kamir_signed_name: kamirMeno,
        dodaci_list: dodaciList, teplota, pocasie, celkovo_m3: sumM3, celkovo_m2: sumM2, ...h }
      const r = editId
        ? await fetchAuth('/api/poter-protokoly/' + editId, { method: 'PUT', body: JSON.stringify(body) })
        : await fetchAuth('/api/poter-protokoly', { method: 'POST', body: JSON.stringify(body) })
      const j = await r.json(); if (!r.ok) throw new Error(j.error || 'Zlyhalo')
      const id = j.id || editId
      setSavedId(id); setEditId(id)
      setDiaryWritten(!!(j.diary_entry_id && String(j.diary_entry_id).trim()))
      toast.success(editId ? 'Protokol upravený: ' + id : 'Protokol uložený: ' + id)
      const pr = await fetchAuth('/api/poter-protokoly/' + id + '/generate-pdf', { method: 'POST' }); const pj = await pr.json()
      if (pj.url) setPdfUrl(pj.url)
    } catch (e) { toast.error('Chyba: ' + e.message) } finally { setBusy(false) }
  }

  const toggle = { display: 'flex', gap: 8 }
  const seg = (on) => ({ flex: 1, maxWidth: 220, textAlign: 'center', padding: 9, borderRadius: RADIUS.md, fontSize: TYPO.sizes.base,
    cursor: 'pointer', fontFamily: 'inherit', fontWeight: TYPO.weights.bold,
    border: `1px solid ${on ? BRAND : COLORS.line}`, background: on ? BRAND : '#fff', color: on ? '#fff' : COLORS.text })
  const cust = fixedProject || projects.find(p => p.id === projId)
  const custEmail = h.email || (cust && (cust.customer_email || cust.email)) || ''
  const mapsHref = h.maps_link || (h.gps_lat ? `https://maps.google.com/?q=${h.gps_lat},${h.gps_lng}` : '')

  return (
    <div style={{ height: '100%', overflowY: 'auto', boxSizing: 'border-box' }}>
      {!embedded && <PageHeader title="Poterový protokol" subtitle="Denník — kontrolný list + podpis zákazníka (MIXIN TL)" />}
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: SPACE.md, display: 'flex', flexDirection: 'column', gap: SPACE.md }}>

        {/* ══ HORNÁ ČASŤ — ZÁKAZKA A KONTAKT (tónovaná karta ako obhliadka) ══ */}
        <div style={{ background: COLORS.bgSoft, border: `1px solid ${COLORS.line}`, borderLeft: `3px solid ${BRAND}`, borderRadius: RADIUS.md, padding: SPACE.md }}>
          <div style={{ fontSize: TYPO.sizes.xs, fontWeight: TYPO.weights.bold, color: BRAND, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: SPACE.sm }}>
            Zákazka a kontakt{editId ? ' · úprava protokolu ' + editId : ''}
          </div>
          <FieldGrid>
            <Field label="Názov *">
              <input style={inp} value={h.nazov} onChange={e => setHF('nazov', e.target.value)} placeholder="napr. RD Trybulová" />
            </Field>
            <Field label="Zákazka (povinná) *">
              {fixedProject
                ? <input style={{ ...inp, background: COLORS.bgSoft, color: COLORS.muted }} readOnly value={(fixedProject.zakazka_code ? fixedProject.zakazka_code + ' · ' : '') + (fixedProject.name || fixedProject.id)} />
                : <SearchableSelect value={projId} onChange={setProjId} options={projOpts} placeholder="Napíš meno / kód zákazky…" menuPortal />}
            </Field>
            <Field label="Zákazník (číselník)">
              <div style={{ display: 'flex', gap: 6 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <SearchableSelect value={h.customer_id} onChange={v => { const c = customers.find(x => x.id === v); if (c) pickCustomer(v); else if (v && String(v).trim()) { setNovyPrefill(String(v).trim()); setNovyOpen(true) } else setHF('customer_id', '') }}
                    options={customers.map(c => ({ value: c.id, label: `${c.name} (${c.id})` }))} placeholder="Hľadať podľa mena alebo IČO…" menuPortal allowCreate />
                </div>
                <button type="button" onClick={() => { setNovyPrefill(h.kontakt_meno || ''); setNovyOpen(true) }}
                  style={{ padding: '0 12px', border: `1px solid ${BRAND}`, background: '#fff', color: BRAND, borderRadius: RADIUS.md, cursor: 'pointer', fontWeight: TYPO.weights.semibold, fontSize: TYPO.sizes.sm, fontFamily: 'inherit', whiteSpace: 'nowrap' }}>+ Nový</button>
              </div>
            </Field>
            {/* ústava 131.10 + 0.J (p93): JEDEN blok kontaktnej osoby v celom Matrixe — osoba z registra partnera */}
            <KontaktnaOsoba customerId={h.customer_id} inp={inp}
              osoba={{ meno: h.kontakt_meno, telefon: h.telefon, email: h.email }}
              onZmena={o => setH(s => ({ ...s, kontakt_meno: o.meno, telefon: o.telefon, email: o.email }))}
              obal={(tx, pole, kl) => <Field key={kl} label={tx}>{pole}</Field>} />
            <Field label="E-mail 2 (kópia)"><input style={inp} type="email" value={h.email2} onChange={e => setHF('email2', e.target.value)} placeholder="napr. druhý kontakt na stavbe" /></Field>
            <Field label="Adresa stavby"><input style={inp} value={h.adresa} onChange={e => setHF('adresa', e.target.value)} placeholder="ulica, mesto" /></Field>
            <Field label="GPS (lat, lng)">
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <input style={inp} value={h.gps_lat} onChange={e => setHF('gps_lat', e.target.value)} placeholder="48.99" />
                <input style={inp} value={h.gps_lng} onChange={e => setHF('gps_lng', e.target.value)} placeholder="21.24" />
                {mapsHref && <a href={mapsHref} target="_blank" rel="noreferrer" style={{ fontSize: TYPO.sizes.sm, color: COLORS.nav, whiteSpace: 'nowrap' }}>mapa →</a>}
              </div>
            </Field>
            <Field label="Google Maps link (voliteľné)"><input style={inp} value={h.maps_link} onChange={e => setHF('maps_link', e.target.value)} placeholder="vlož zdieľaný pin" /></Field>
            <Field label="Termín realizácie"><input style={inp} value={h.termin_real} onChange={e => setHF('termin_real', e.target.value)} placeholder="napr. 08/2026" /></Field>
            <Field label="Partia (z Rozpisu)">
              <SearchableSelect value={h.partia_id} onChange={v => { const p = partie.find(x => x.value === v); setH(s => ({ ...s, partia_id: v, partia_nazov: p ? p.label : '' })) }}
                options={partie} placeholder="Vyber partiu, ktorá leje…" menuPortal />
            </Field>
          </FieldGrid>
        </div>

        {/* ══ SPODNÁ ČASŤ — samotný protokol (biela karta, Grp sekcie ako obhliadka) ══ */}
        <div style={{ background: COLORS.bg, border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.md, padding: SPACE.md }}>

          {/* prepínač Anhydrit / Cement — ako mview */}
          <div style={toggle}>
            <div style={seg(typ === 'anhydrit')} onClick={() => setTyp('anhydrit')}>Anhydrit</div>
            <div style={seg(typ === 'cement')} onClick={() => setTyp('cement')}>Cement</div>
          </div>

          <Grp>Objednávateľ a materiál</Grp>
          <FieldGrid>
            <Field label="Objednávateľ (zo zákazky)">
              <Chips val={objTyp} onPick={v => v && setObjTyp(v)} opts={[{ value: 'rd', label: 'RD' }, { value: 'firma', label: 'Firma' }]} />
            </Field>
            {typ === 'anhydrit' && (
              <Field label="Materiál (MIXIN, z CP)">
                <Chips val={matCode} onPick={v => v && setMatCode(v)}
                  opts={(meta.materials.anhydrit || []).map(m => ({ value: m.code, label: `${m.name} (${m.cls})` }))} />
              </Field>
            )}
            {material && (
              <Field label="Minimálna hrúbka nad kúrením podľa výrobcu">
                <div style={{ ...inp, background: COLORS.bgSoft }}>{material.min_mm} mm{matCode === 'THERMIO' ? ' (20 nestlač. / 25 stlač.)' : ''}</div>
              </Field>
            )}
          </FieldGrid>

          <Grp>Podlažia</Grp>
          {podlazia.map((p, i) => {
            const mh = dec(p.min_hrubka_mm)
            const ref = material ? material.min_mm : null
            const ok = mh != null && ref != null ? mh >= ref : null
            return (
              <div key={i} style={{ border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.md, padding: SPACE.md, marginBottom: SPACE.sm, background: '#fff' }}>
                <FieldGrid>
                  <Field label="Podlažie">
                    <select value={p.podlazie} onChange={e => setPod(i, 'podlazie', e.target.value)} style={inp}>
                      <option value="">— podlažie —</option>
                      {floors.map(f => <option key={f} value={f}>{f}</option>)}
                    </select>
                  </Field>
                  <Field label="Dohodnutá výška (vagrys)"><input style={inp} value={p.dohodnuta_vyska_cm} onChange={e => setPod(i, 'dohodnuta_vyska_cm', e.target.value)} inputMode="decimal" placeholder="cm" /></Field>
                  <Field label="Nameraná hrúbka v najtenšom mieste">
                    <input style={{ ...inp, borderColor: ok === false ? '#B42318' : COLORS.line }} value={p.min_hrubka_mm} onChange={e => setPod(i, 'min_hrubka_mm', e.target.value)} inputMode="decimal" placeholder="mm" />
                    <div style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted, marginTop: 3 }}>meraj najtenšie miesto, nie priemer — záruka sa posudzuje podľa neho</div>
                  </Field>
                  <Field label="Vyčerpané"><input style={inp} value={p.vycerpane_m3} onChange={e => setPod(i, 'vycerpane_m3', e.target.value)} inputMode="decimal" placeholder="m³" /></Field>
                  <Field label="Plocha"><input style={inp} value={p.plocha_m2} onChange={e => setPod(i, 'plocha_m2', e.target.value)} inputMode="decimal" placeholder="m²" /></Field>
                  <Field label="Popis (voliteľné)"><input style={inp} value={p.popis} onChange={e => setPod(i, 'popis', e.target.value)} /></Field>
                </FieldGrid>
                {ok != null && (
                  <div style={{ fontSize: TYPO.sizes.sm, margin: '6px 0', fontWeight: TYPO.weights.semibold, color: ok ? '#0F6E56' : '#B42318' }}>
                    {ok
                      ? `Najtenšie miesto ${num(mh)} mm — vyhovuje, výrobca žiada minimálne ${ref} mm.`
                      : `Najtenšie miesto má ${num(mh)} mm. Výrobca ${material ? material.name : 'materiálu'} vyžaduje nad kúrením minimálne ${ref} mm — chýba ${num(ref - mh)} mm. Na takto tenkú vrstvu NEVIEME poskytnúť záruku. Riešenie: zvýšiť vrstvu, alebo použiť pevnejší materiál (C30 / Thermio).`}
                  </div>
                )}
                {typ === 'anhydrit' && <div style={{ marginTop: 6 }}>{/* PROTOKOL_KONZISTENCIA_V1 */}
                  <div style={{ fontSize: TYPO.sizes.xs, fontWeight: TYPO.weights.semibold, color: (p.foto_konzistencia || []).length ? COLORS.muted : '#B42318', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Skúška konzistencie — rozlievací terč ({(p.foto_konzistencia || []).length}){(p.foto_konzistencia || []).length ? '' : ' · povinné'}</div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginTop: 5 }}>
                    {(p.foto_konzistencia || []).map((u, k) => (
                      <span key={k} style={{ position: 'relative', display: 'inline-block' }}>
                        <a href={u} target="_blank" rel="noreferrer"><img src={u} alt="" style={{ width: 96, height: 70, objectFit: 'cover', borderRadius: 6, border: `1px solid ${COLORS.line}` }} /></a>
                        <button type="button" onClick={() => delKonz(i, k)} title="Odobrať fotku" style={{ position: 'absolute', top: -6, right: -6, width: 20, height: 20, borderRadius: 10, border: `1px solid ${COLORS.line}`, background: '#fff', cursor: 'pointer', fontSize: 11, lineHeight: '17px', color: COLORS.muted }}>×</button>
                      </span>
                    ))}
                    <label style={{ width: 96, height: 70, border: `1px dashed ${BRAND}`, borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: BRAND, fontSize: TYPO.sizes.xs, fontWeight: TYPO.weights.semibold }}>
                      + Pridať
                      <input type="file" accept="image/*" capture="environment" onChange={e => { const fs = e.target.files; e.target.value = ''; uploadKonz(i, fs) }} style={{ display: 'none' }} />
                    </label>
                  </div>
                </div>}
                <div style={{ fontSize: TYPO.sizes.xs, fontWeight: TYPO.weights.semibold, color: COLORS.muted, textTransform: 'uppercase', letterSpacing: '0.04em', marginTop: 6 }}>Fotodokumentácia ({(p.foto || []).length})</div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginTop: 5 }}>
                  {(p.foto || []).map((u, k) => (
                    <span key={k} style={{ position: 'relative', display: 'inline-block' }}>
                      <a href={u} target="_blank" rel="noreferrer"><img src={u} alt="" style={{ width: 96, height: 70, objectFit: 'cover', borderRadius: 6, border: `1px solid ${COLORS.line}` }} /></a>
                      <button type="button" onClick={() => delFoto(i, k)} title="Odobrať fotku"
                        style={{ position: 'absolute', top: -6, right: -6, width: 20, height: 20, borderRadius: 10, border: `1px solid ${COLORS.line}`, background: '#fff', cursor: 'pointer', fontSize: 11, lineHeight: '17px', color: COLORS.muted }}>✕</button>
                    </span>
                  ))}
                  <label style={{ width: 96, height: 70, border: `1px dashed ${BRAND}`, borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: BRAND, fontSize: TYPO.sizes.xs, fontWeight: TYPO.weights.bold }}>
                    + Pridať
                    <input type="file" accept="image/*" capture="environment" multiple onChange={e => { const fs = e.target.files; e.target.value = ''; uploadFoto(i, fs) }} style={{ display: 'none' }} />
                  </label>
                  {podlazia.length > 1 && <button type="button" onClick={() => delPod(i)} style={{ marginLeft: 'auto', border: `1px solid ${COLORS.line}`, background: '#fff', borderRadius: 8, padding: '6px 12px', cursor: 'pointer', color: COLORS.remove, fontFamily: 'inherit', fontSize: TYPO.sizes.sm }}>✕ Odobrať podlažie</button>}
                </div>
              </div>
            )
          })}
          <div onClick={addPod} style={{ textAlign: 'center', padding: 9, border: `1px dashed ${COLORS.line}`, borderRadius: 8, color: COLORS.muted, fontSize: TYPO.sizes.sm, cursor: 'pointer' }}>+ Pridať podlažie</div>
          <div style={{ textAlign: 'right', fontSize: TYPO.sizes.sm, marginTop: 8 }}>Celkovo: <b>{num(sumM3)} m³</b> · <b>{num(sumM2)} m²</b></div>

          <Grp>Kontrolný list — potvrď pred/pri liatí</Grp>
          <div style={{ border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.md, padding: SPACE.sm, background: '#fff' }}>
            {(meta.checklist[typ] || []).map(it => (
              <label key={it.key} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', padding: '5px 0', fontSize: TYPO.sizes.sm, cursor: 'pointer' }}>
                <input type="checkbox" checked={!!checks[it.key]} onChange={e => setChecks(c => ({ ...c, [it.key]: e.target.checked }))} style={{ width: 16, height: 16, marginTop: 2, flexShrink: 0 }} />
                <span>{it.label}</span>
              </label>
            ))}
          </div>

          <Grp>Podmienky pri liatí (dôkaz pri reklamácii)</Grp>
          <FieldGrid>
            <Field label="Číslo dodacieho listu"><input style={inp} value={dodaciList} onChange={e => setDodaciList(e.target.value)} /></Field>
            <Field label="Počasie"><input style={inp} value={pocasie} onChange={e => setPocasie(e.target.value)} placeholder="sucho, dážď…" /></Field>
            <Field label="Teplota">
              <div style={{ display: 'flex', gap: 6 }}>
                <input style={inp} value={teplota} onChange={e => setTeplota(e.target.value)} placeholder={pocasieBusy ? 'načítavam…' : '°C'} />
                <button type="button" onClick={() => loadPocasie(false)} disabled={pocasieBusy}
                  style={{ border: `1px solid ${COLORS.line}`, background: '#fff', borderRadius: RADIUS.md, cursor: 'pointer', fontSize: TYPO.sizes.sm, padding: '0 10px', color: COLORS.muted, fontFamily: 'inherit', whiteSpace: 'nowrap' }}>načítať</button>
              </div>
            </Field>
          </FieldGrid>
          <div style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted, marginTop: 4 }}>Teplota sa načíta sama z adresy alebo GPS (ústava 25.6); ak sa nedá, doplň ručne.</div>

          <Grp>Záver</Grp>
          <Field label="Poznámka k protokolu (voliteľné)">
            <textarea style={{ ...inp, resize: 'vertical', minHeight: 56 }} value={popis} onChange={e => setPopis(e.target.value)} />
          </Field>

          <div style={{ border: '1px solid #B42318', background: '#fdecea', borderRadius: RADIUS.md, padding: SPACE.sm, marginTop: SPACE.sm }}>
            <div style={{ fontSize: TYPO.sizes.sm, color: '#7a1c12', marginBottom: 8, whiteSpace: 'pre-line' }}>{/* PROTOKOL_POVINNOSTI_V1 */ typ === 'cement' ? (meta.waiver_zakladny || meta.waiver) : meta.waiver}</div>
            <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: TYPO.sizes.sm, fontWeight: TYPO.weights.semibold, color: '#7a1c12', cursor: 'pointer' }}>
              <input type="checkbox" checked={waiverOk} onChange={e => setWaiverOk(e.target.checked)} /> Zákazník oboznámený a súhlasí
            </label>
          </div>

          <Grp>Podpis zákazníka (voliteľné)</Grp>
          <SignaturePad onChange={setSignature} />
          <Field label="Meno zákazníka (voliteľné)">
            <input style={inp} value={signedName} onChange={e => setSignedName(e.target.value)} />
          </Field>
          <div style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted, marginTop: 4 }}>Za KAMIR sa doplní prihlásený používateľ a dátum automaticky.</div>
        </div>

        <HistoriaZmien id={editId} />   {/* STAVBA_HISTORIA_V1 */}

        {/* ══ ULOŽIŤ ══ */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <BtnAdd onClick={save} disabled={busy}>{busy ? 'Ukladám…' : (editId ? '✓ Uložiť zmeny' : 'Uložiť protokol')}</BtnAdd>
          {editId && <span style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted }}>Upravuješ protokol {editId} — fotky zostanú zachované.</span>}
        </div>

        {savedId && (
          <div style={{ border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.md, padding: SPACE.md, display: 'flex', flexDirection: 'column', gap: 10, background: '#fff' }}>
            <div style={{ fontSize: TYPO.sizes.sm, fontWeight: TYPO.weights.semibold, color: '#0F6E56' }}>Protokol {savedId} uložený</div>
            {pdfUrl && <a href={pdfUrl} target="_blank" rel="noreferrer" style={{ fontSize: TYPO.sizes.sm, color: COLORS.nav }}>Otvoriť / stiahnuť PDF</a>}
            {/* PROTOKOL_APLIKACIA_V1 (Mirko 21. 10. 2026): bez fotky konzistencie sa odošle, len upozornenie */ typ === 'anhydrit' && !podlazia.some(p => (p.foto_konzistencia || []).length)
              && <div style={{ fontSize: TYPO.sizes.sm, color: '#B42318', fontWeight: TYPO.weights.semibold }}>Chýba fotka skúšky konzistencie (rozlievací terč) — je povinná, doplň ju pri podlaží a ulož. Odoslať sa dá aj bez nej.</div>}
            {<EmailReplyBox
              target={{ project_id: projId }}
              defaultTo={custEmail}
              defaultSubject={'Poterový protokol ' + savedId}
              defaultText={'Dobrý deň,\n\nv prílohe posielame poterový protokol ' + savedId + '.\n\nS pozdravom'}
              attach={pdfUrl ? [{ url: pdfUrl, filename: 'Poterovy_protokol_' + savedId + '.pdf' }] : []}
              label="Odoslať zákazníkovi e-mailom"
            />}
            <div style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted }}>{objTyp === 'firma' ? 'Firma: odoslať môže office aj majiteľ · slúži aj ako podklad pre súpis/odovzdávací protokol.' : 'RD: odoslať môže majiteľ aj office.'} PDF protokolu sa priloží automaticky.</div>
            <div style={{ fontSize: TYPO.sizes.xs, color: '#0F6E56' }}>{diaryWritten ? 'Zapísané aj do stavebného denníka (denný záznam realizácie poteru).' : 'Samostatný protokol — bez zápisu do denníka (jednorázová zákazka).'}</div>
            {onSaved && <Btn onClick={() => onSaved()}>✓ Hotovo</Btn>}
          </div>
        )}
      </div>

      <NoviCustomerDialog open={novyOpen} prefillName={novyPrefill}
        onClose={() => { setNovyOpen(false); setNovyPrefill('') }}
        onCreated={c => { setCustomers(prev => [...prev, c]); pickCustomer(c.id); setNovyOpen(false); setNovyPrefill('') }} />
    </div>
  )
}
